import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {Container} from 'constants/SampleConstants';
import {TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {useExistingTestOrdersForSamples} from 'sample-tests/ui/test-orders/modals/useExistingTestOrders';
import {BasicTestOrder, getTestOrdersSearchUrl} from 'sample-tests/ui/utils/testOrderSearchUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import StudyChip from 'ui-library/chips/StudyChip';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

import {TestOrderCreateInfo} from '../buttons/CreateTestOrdersButton';
import {CreateTestOrdersModalQuery} from './__generated/CreateTestOrdersModalQuery';
import {CreateTestOrdersProcedureMethodDefinitionVersionFragment_ProcedureMethodDefinitionVersionNode_testDefinitions_TestDefinitionNode as TestDefinitionNode} from './__generated/CreateTestOrdersProcedureMethodDefinitionVersionFragment';
import SampleAssayTemplateSelectionTable from './SampleAssayTemplateSelectionTable';
import {AllowedDilutionFactor, QueryingTestOrderCreationTable} from './TestOrderCreationTable';

type MaybeInaccessibleProcedure = CreateTestOrdersModalQuery['procedure'];
export type ProcedureWithTestDefinitions = ExtractByTypename<MaybeInaccessibleProcedure, 'ProcedureNode'>;
export type TestDefinitionForModal = TestDefinitionNode;

/**
 * Props for the CreateTestOrdersModal component.
 *
 * @property {Array<string>} studyIds - Non-empty array of study IDs. The first study ID will be used to create the test orders.
 */
export interface CreateTestOrdersModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (testOrders: Array<TestOrderCreateInfo>) => Promise<void>;
  sampleIds: Array<string>;
  studyIds: Array<string>;
}

const initializeBySample = (sampleIds: Array<string>): Record<string, string | null> =>
  Object.fromEntries(sampleIds.map((sampleId) => [sampleId, null]));

const getModalButtons = (
  selectedTestOrderData: Array<TestOrderCreationRowData>,
  isSubmitting: boolean
): Array<ModalButton> => {
  const hasErrors = selectedTestOrderData.some((row) => row.hasDilutionFactorError);
  return [
    {
      text: isSubmitting
        ? 'Creating...'
        : selectedTestOrderData.length > 0
          ? `Create ${selectedTestOrderData.length} test orders`
          : 'Create',
      submit: true,
      disabled: selectedTestOrderData.length === 0 || hasErrors || isSubmitting,
      tooltipTitle:
        selectedTestOrderData.length === 0
          ? 'Please select test orders to create'
          : hasErrors
            ? 'Some test orders are missing a dilution factor'
            : `Create ${selectedTestOrderData.length} test orders`,
    },
  ];
};

const getTestOrdersSummaryBySampleId = (
  selectedTestOrderData: Array<TestOrderCreationRowData>
): {countMap: Record<string, number>; errorMap: Record<string, string>} => {
  const countMap: Record<string, number> = {};
  const errorMap: Record<string, string> = {};

  selectedTestOrderData.forEach((row) => {
    const sampleId = row.sample.id;
    countMap[sampleId] = (countMap[sampleId] || 0) + 1;

    if (row.hasDilutionFactorError) {
      errorMap[sampleId] = 'Some rows are missing a dilution factor';
    }
  });

  return {countMap, errorMap};
};

const generateTestOrderInfos = (
  selectedTestOrderData: Array<TestOrderCreationRowData>,
  studyId: string
): Array<TestOrderCreateInfo> =>
  selectedTestOrderData.map((row) => ({
    sampleId: row.sample.id,
    procedureId: row.assayTemplateId,
    testDefinitionId: row.testDefinition.id,
    dilutionFactor: row.dilutionFactor,
    containerId: row.container?.id ?? null,
    studyIds: [studyId],
  }));

export interface TestDefinitionSimpleInfo {
  id: string;
  name: string;
}

export interface TestOrderCreationRowData {
  rowId: string; // sampleId + testDefinitionId, generated in client/sample-tests/ui/test-orders/modals/useTestOrderCreationTable.tsx
  sample: {id: string; name?: string};
  assayTemplateId: string;
  testDefinition: TestDefinitionSimpleInfo;
  dilutionFactor: AllowedDilutionFactor | null;
  hasDilutionFactorError: boolean; // if dilutionFactor is null but assayTemplate has a dilution plan making it required
  // Any existing (open) test orders corresponding to this sample/test definition
  existingTestOrders: Array<BasicTestOrder>;
  // Warning for existing test orders; only applicable when row is selected
  existingTestOrdersWarning: string | null;
  container: Container | null;
  isSelected: boolean;
}

export default function CreateTestOrdersModal({
  onRequestClose,
  onRequestSubmit,
  sampleIds,
  studyIds,
}: CreateTestOrdersModalProps): JSX.Element {
  assert(studyIds.length > 0, 'At least one study must selected for creating test orders');
  const primaryStudyId = studyIds[0]; // TODO(BNCH-108432): support samples with multiple studies

  const [assayTemplateBySampleId, setAssayTemplateBySampleId] = useState<Record<string, string | null>>(
    initializeBySample(sampleIds)
  );
  const [fullTestOrderData, setFullTestOrderData] = useState<Array<TestOrderCreationRowData>>([]);

  const [selectedSampleId, setSelectedSampleId] = useState<string>(sampleIds[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedTestOrderData = useMemo(
    () => fullTestOrderData.filter((row) => row.isSelected),
    [fullTestOrderData]
  );

  const existingTestOrdersBySampleTestDefinition = useExistingTestOrdersForSamples(sampleIds);
  // Pull out all test orders for rows with existing test order warnings so that we include the right set
  // of test orders in the warning banner's search link
  const duplicateTestOrders = selectedTestOrderData.flatMap((row) =>
    row.existingTestOrdersWarning != null ? row.existingTestOrders : []
  );

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    const testOrderInfos = generateTestOrderInfos(selectedTestOrderData, primaryStudyId);
    await onRequestSubmit(testOrderInfos);
    setIsSubmitting(false);
  };

  const handleClose = (): void => {
    onRequestClose();
  };

  const selectedAssayTemplateId = useMemo(
    () => assayTemplateBySampleId[selectedSampleId] ?? null,
    [assayTemplateBySampleId, selectedSampleId]
  );

  const {countMap: testOrderCountBySampleId, errorMap: errorsBySampleId} = useMemo(
    () => getTestOrdersSummaryBySampleId(selectedTestOrderData),
    [selectedTestOrderData]
  );

  return (
    <Modal
      title={
        <div className={css(styles.titleWrapper)}>
          <span>Create test orders</span>
        </div>
      }
      mediumLarge={true}
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      buttons={getModalButtons(selectedTestOrderData, isSubmitting)}
      closeable={true}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
      expandHeight={true}
      dataTestAttrs={{component: 'CreateTestOrdersModal', element: 'root'}}
    >
      {studyIds.length > 1 && (
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={
            <div>
              The test orders created will be associated with
              <StudyChip id={primaryStudyId} size={ChipSize.MINI} />
            </div>
          }
        />
      )}
      <div className={css(styles.sampleTable)}>
        <SampleAssayTemplateSelectionTable
          // TODO(BNCH-108432): support samples with multiple studies
          studyIds={[primaryStudyId]}
          sampleIds={sampleIds}
          selectedSampleId={selectedSampleId}
          setSelectedSampleId={setSelectedSampleId}
          testOrderCountBySampleId={testOrderCountBySampleId}
          errorsBySampleId={errorsBySampleId}
          assayTemplateBySampleId={assayTemplateBySampleId}
          setAssayTemplateBySampleId={setAssayTemplateBySampleId}
          tableHelpText={
            <p className={css(styles.modalText)}>
              Select an assay to view available test orders. Click "create" to generate test orders for all
              samples.
            </p>
          }
        />
      </div>
      {duplicateTestOrders.length > 0 && (
        <div className="u-small-spacing-bottom">
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'View test orders',
              // Study id intentionally omitted to show duplicate test orders across multiple studies
              actionHref: getTestOrdersSearchUrl(duplicateTestOrders, undefined, [
                TestOrderStatus.PENDING,
                TestOrderStatus.IN_PROGRESS,
                TestOrderStatus.BLOCKED,
              ]),
            }}
            text="Some samples already have test orders."
          />
        </div>
      )}
      {selectedAssayTemplateId && (
        <QueryingTestOrderCreationTable
          procedureId={selectedAssayTemplateId}
          selectedSampleId={selectedSampleId}
          fullTestOrderData={fullTestOrderData}
          setFullTestOrderData={setFullTestOrderData}
          tableHeader={
            <div className={css(styles.tableLabel)}>
              <p className={css(styles.tabelLabelText, styles.modalText)}>
                Available test orders to create for
              </p>
              <BenchChip className={css(styles.sampleChip)} id={selectedSampleId} size={ChipSize.MINI} />
            </div>
          }
          existingTestOrdersBySampleTestDefinition={existingTestOrdersBySampleTestDefinition}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  titleWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '50px',
    rowGap: 10,
  },
  modalText: {
    fontSize: '13px',
    fontWeight: 400,
  },
  sampleTable: {
    paddingBottom: '18px',
  },
  tableLabel: {
    display: 'flex',
    alignItems: 'baseline',
  },
  tabelLabelText: {
    paddingRight: '6px',
  },
  sampleChip: {
    paddingTop: '2px',
  },
  bannerText: {
    paddingBottom: '8px',
  },
});
